"use client";

import Image from "next/image";
import type { Spirit } from "@/lib/types/spirit";
import Glyph from "@/components/ui/Glyph";
import TextLines from "@/components/ui/TextLines";
import { faceFocusStyle } from "@/lib/spirit-face";
import { GENRE_COLORS, type GenreKey } from "@/lib/genre-palette";
import { useUiTexts } from "@/lib/ui-texts-client";

interface Props {
  spirits: Spirit[];
  handSlotCount: number;
  onClick: () => void;
}

/** ホームの帯に並べる区切りの数の上限。手持ちの枠がもっと増えても（将来は6体まで）、ホームは4つまで */
const HOME_STRIP_MAX = 4;

/**
 * ホームの精霊のしおり（2026-10-12 本人の手描きの見本 /mnt/d/無限図書館/司書/ソシャゲ参考画像/IMG_1434.jpeg）。
 * 左に穴と房付きの紐（globals.css の .library-companions > button）、その右を手持ちの枠の数（4つまで）だけ斜めに区切り、区切りごとに精霊の絵を載せる。
 * 精霊のいない枠は空白のまま残し、空きの数（ホームに見えない枠も数える）を「空きはあと〇枠」と出す。最初の空いた枠の中、見えないとき（4体が埋まっていて、さらに空きがある）は帯の右端の先（しおりの中・見えない枠が続く方向）に同じ2行で（10/12 本人の指示）。
 * 区切りの線は帯の地が隙間から見えるもの。区切りの地は精霊の属性の色（10/12 本人「背景に属性の色を」）。絵は精霊ごとの顔の位置（spiritMaster の faceFocus・管理UIで決める）に寄せる。数と名前は読み上げにだけ出す
 */
export default function SpiritStripBookmark({ spirits, handSlotCount, onClick }: Props) {
  const { t } = useUiTexts();
  const shown = spirits.slice(0, HOME_STRIP_MAX);
  const emptyTotal = Math.max(0, handSlotCount - spirits.length);
  const emptyShown = Math.max(0, Math.min(HOME_STRIP_MAX, Math.max(spirits.length, handSlotCount)) - shown.length);
  const freeText = emptyTotal > 0 ? t("home.companions.freeSlots", { 枠: emptyTotal }) : "";
  const label = `${t("spirit.partyRow.title", { 数: spirits.length, 枠: handSlotCount })} ${spirits.map((s) => s.name || t("spirit.common.noName")).join("・")}${freeText ? ` ${freeText.replace(/\n/g, "")}` : ""}`;
  return (
    <button type="button" onClick={onClick} aria-label={label} className="library-spirit-strip-button">
      <span className="library-spirit-strip" style={{ "--n": shown.length + emptyShown } as React.CSSProperties} aria-hidden="true">
        {shown.map((spirit, i) => {
          // 顔の中心を枠の真ん中へ寄せて拡大する（決めていない精霊は真ん中の上寄りを2倍）
          const face = faceFocusStyle(spirit.faceFocus);
          const genre = GENRE_COLORS[spirit.genre as GenreKey];
          return (
            <span
              key={spirit.id}
              className="library-spirit-strip-panel"
              data-away={spirit.isOnAdventure ? "" : undefined}
              // 地は精霊の属性の色（genre-palette）。知らない属性は紫のまま
              style={{ "--i": i, ...(genre && { "--genre": genre }) } as React.CSSProperties}
            >
              {spirit.imageUrl ? (
                spirit.avatarType === "video" ? (
                  <video src={spirit.imageUrl} loop autoPlay muted playsInline className="h-full w-full object-cover" style={face} />
                ) : (
                  <Image src={spirit.imageUrl} alt="" fill sizes="320px" className="object-cover" style={face} />
                )
              ) : (
                <span className="grid h-full w-full place-items-center text-lg"><Glyph e="✨" /></span>
              )}
            </span>
          );
        })}
        {/* 空いている手持ちの枠。最初の空いた枠に空きの数 */}
        {Array.from({ length: emptyShown }, (_, j) => (
          <span key={`empty-${j}`} className="library-spirit-strip-panel" data-empty="" style={{ "--i": shown.length + j } as React.CSSProperties}>
            {j === 0 && freeText && <span className="library-spirit-strip-free"><TextLines text={freeText} /></span>}
          </span>
        ))}
      </span>
      {/* 空いた枠がホームに見えないとき（4体が埋まっていて5・6枠目が空いている）は、帯の右端の先に空きの数 */}
      {freeText && emptyShown === 0 && <span className="library-spirit-strip-more" aria-hidden="true"><TextLines text={freeText} /></span>}
    </button>
  );
}
